import DiscountIcon from "@mui/icons-material/Discount";
import { Dialog, Grid } from "@mui/material";
import { styled } from "@mui/material/styles";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import React, { forwardRef, useImperativeHandle, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import AppLoader from "../../../../components/AppLoader";
import DialogBody from "../../../../components/Dialog/Body";
import DialogActionButtons from "../../../../components/Dialog/Footer";
import DialogHeader from "../../../../components/Dialog/Header";
import Label from "../../../../components/Label";
import TextInput from "../../../../components/TextInputs";
import { setPromoCodeCurrentPage } from "../../../../lib/slices/pagination/promoCodePageSlice";
import { createPromoCode, updatePromoCode } from "../../../../services/promoCodeApiFn";
import { handleOpenSnackBar } from "../../../../utils";
import { CURRENCY } from "../../../../constants/Variables";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": { padding: theme.spacing(4, 2) },
  "& .MuiDialogActions-root": { padding: theme.spacing(2) },
  "& .MuiPaper-root": { [theme.breakpoints.down("sm")]: { margin: 12 } },
}));

let paramsData = {};

const initialInputValues = {
  code: "",
  description_en: "",
  description_ro: "",
  discountType: "percentage",
  discountValue: "",
  maxDiscountAmount: "",
  minOrderAmount: "",
  usageLimit: "",
  startDate: null,
  endDate: null,
};

const validatePromoCode = (values, setInputError) => {
  const errors = {};
  if (!values.code.trim()) errors.code = "Promo code is required";
  if (!values.description_ro.trim()) errors.description_ro = "Romanian description is required";
  if (!values.description_en.trim()) errors.description_en = "English description is required";
  if (!values.discountValue || values.discountValue < 0 || values.discountValue > 100) {
    errors.discountValue = "Discount must be between 0 and 100%";
  }
  if (!values.minOrderAmount || values.minOrderAmount < 0) {
    errors.minOrderAmount = "Minimum order amount is required";
  }
  if (!values.usageLimit || values.usageLimit < 1) errors.usageLimit = "Usage limit must be at least 1";
  if (!values.startDate) errors.startDate = "Start date is required";
  if (!values.endDate) errors.endDate = "End date is required";
  if (values.startDate && values.endDate && dayjs(values.startDate).isAfter(values.endDate)) {
    errors.startDate = "Start date must be before end date";
  }
  setInputError(errors);
  return Object.keys(errors).length > 0;
};

export const AddEditPromoCodeDialog = forwardRef(({}, ref) => {
  const dispatch = useDispatch();
  const { t } = useTranslation(["catalogue", "common"]);
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [inputError, setInputError] = useState({});
  const [inputValues, setInputValues] = useState(initialInputValues);
  const searchKeys = useSelector((state) => state.searchKeysReducer.searchKeys);
  const pagination = useSelector((state) => state.promoCodePaginationReducer);

  useImperativeHandle(ref, () => ({
    openDialog(params) {
      paramsData = params;
      setOpen(true);
      if (params.type === "edit") {
        const data = params.data;
        setInputValues({
          code: data?.code || "",
          description_en: data?.description?.en || "",
          description_ro: data?.description?.ro || "",
          discountType: "percentage",
          discountValue: data?.discountValue ?? "",
          maxDiscountAmount: data?.maxDiscountAmount ?? "",
          minOrderAmount: data?.minOrderAmount ?? "",
          usageLimit: data?.usageLimit ?? "",
          startDate: data?.startDate ? dayjs(data.startDate) : null,
          endDate: data?.endDate ? dayjs(data.endDate) : null,
        });
      }
    },
    closeDialog() {
      handleClose();
    },
  }));

  const handleClose = () => {
    setOpen(false);
    setInputValues(initialInputValues);
    setInputError({});
  };

  const handleChangeInputs = (event) => {
    const { name, value, valueAsNumber, type } = event.target;
    setInputValues((previous) => ({
      ...previous,
      [name]: type === "number" ? valueAsNumber : value,
    }));
    setInputError((previous) => ({ ...previous, [name]: "" }));
  };

  const handleDateChange = (date, name) => {
    setInputValues((previous) => ({ ...previous, [name]: date }));
    setInputError((previous) => ({ ...previous, [name]: "" }));
  };

  const handleAddEdit = async () => {
    if (validatePromoCode(inputValues, setInputError)) {
      return handleOpenSnackBar("error", "Please resolve the highlighted issues");
    }

    const payload = {
      code: inputValues.code.trim(),
      description: {
        en: inputValues.description_en.trim(),
        ro: inputValues.description_ro.trim(),
      },
      discountType: "percentage",
      discountValue: Number(inputValues.discountValue),
      maxDiscountAmount: inputValues.maxDiscountAmount
        ? Number(inputValues.maxDiscountAmount)
        : null,
      minOrderAmount: Number(inputValues.minOrderAmount),
      usageLimit: Number(inputValues.usageLimit),
      startDate: inputValues.startDate,
      endDate: inputValues.endDate,
    };

    try {
      setIsLoading(true);
      const response = paramsData?.type === "add"
        ? await createPromoCode("", payload)
        : await updatePromoCode(paramsData?.data?._id, payload);

      if (response?.data?.status === "success") {
        handleOpenSnackBar("success", response.data.message);
        if (paramsData?.type === "add") dispatch(setPromoCodeCurrentPage(1));
        handleClose();
        paramsData?.getData(
          searchKeys.promoCode,
          paramsData?.type === "add" ? 1 : pagination.page,
          pagination.limit,
        );
      } else {
        handleOpenSnackBar("error", response?.data?.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", t("error.somethingWentWrong", { ns: "common" }));
    } finally {
      setIsLoading(false);
    }
  };

  const renderNumberField = (name, label, placeholder, endIcon, required = true) => (
    <Grid item xs={12} md={6}>
      <Label required={required} labelText={label} htmlFor={name} />
      <TextInput
        id={name}
        name={name}
        type="number"
        required={required}
        placeholder={placeholder}
        value={inputValues[name]}
        onChange={handleChangeInputs}
        inputError={Boolean(inputError[name])}
        helperText={inputError[name]}
        InputEndIcon={endIcon}
      />
    </Grid>
  );

  return (
    <>
      <AppLoader isVisible={isLoading} />
      <BootstrapDialog onClose={handleClose} open={open} fullWidth maxWidth="sm">
        <DialogHeader
          title={paramsData.type === "add" ? "Add Promo Code" : "Edit Promo Code"}
          secondaryHeading="Create a percentage discount promo code"
          icon={<DiscountIcon />}
          onClose={handleClose}
        />
        <DialogBody>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Label required labelText="Promo Code" htmlFor="code" />
              <TextInput
                id="code"
                name="code"
                placeholder="Enter promo code"
                value={inputValues.code}
                onChange={handleChangeInputs}
                inputError={Boolean(inputError.code)}
                helperText={inputError.code}
              />
            </Grid>
            <Grid item xs={12}>
              <Label required labelText="Description (Romanian)" htmlFor="description_ro" />
              <TextInput
                id="description_ro"
                name="description_ro"
                placeholder="Enter Romanian description"
                value={inputValues.description_ro}
                onChange={handleChangeInputs}
                inputError={Boolean(inputError.description_ro)}
                helperText={inputError.description_ro}
                multiline
                rows={3}
              />
            </Grid>
            <Grid item xs={12}>
              <Label required labelText="Description (English)" htmlFor="description_en" />
              <TextInput
                id="description_en"
                name="description_en"
                placeholder="Enter English description"
                value={inputValues.description_en}
                onChange={handleChangeInputs}
                inputError={Boolean(inputError.description_en)}
                helperText={inputError.description_en}
                multiline
                rows={3}
              />
            </Grid>
            {renderNumberField("discountValue", "Discount Percentage", "Enter discount percentage", "%")}
            {renderNumberField("maxDiscountAmount", "Maximum Discount Amount", "Enter maximum discount amount", CURRENCY, false)}
            {renderNumberField("minOrderAmount", "Minimum Order Amount", "Enter minimum order amount", CURRENCY)}
            {renderNumberField("usageLimit", "Usage Limit", "Enter usage limit", null)}
            <Grid item xs={12} md={6}>
              <Label required labelText="Start Date" htmlFor="startDate" />
              <DatePicker
                value={inputValues.startDate}
                onChange={(date) => handleDateChange(date, "startDate")}
                slotProps={{
                  textField: {
                    id: "startDate",
                    fullWidth: true,
                    error: Boolean(inputError.startDate),
                    helperText: inputError.startDate,
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Label required labelText="End Date" htmlFor="endDate" />
              <DatePicker
                value={inputValues.endDate}
                onChange={(date) => handleDateChange(date, "endDate")}
                slotProps={{
                  textField: {
                    id: "endDate",
                    fullWidth: true,
                    error: Boolean(inputError.endDate),
                    helperText: inputError.endDate,
                  },
                }}
              />
            </Grid>
          </Grid>
        </DialogBody>
        <DialogActionButtons
          onConfirm={handleAddEdit}
          onCancel={handleClose}
          confirmText="save"
          cancelText="cancel"
          isConfirmBtnDisable={isLoading}
          showCancelButton
        />
      </BootstrapDialog>
    </>
  );
});
